import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { ApolloError } from "@apollo/client";
import { Input, Select, Button, Alert, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Markdown from "react-markdown";

import { useSendRequestServiceMutation } from "graphql/mutations/send-request-service.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useAiChatStatus } from "state/reactive-vars/ai-chat-status-var";
import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import LoadingOverlay from "components/loading-overlay/loading-overlay";

import routeNames from "constants/route-names";

const { TextArea } = Input;

const modalStyles = {
	content: {
		borderRadius: "10px",
		padding: "24px",
		backgroundColor: "#f8f9fa"
	}
};

interface Attachment {
	uid: string;
	filename: string;
	content: string;
	contentType: string;
	file: any;
}

const MAX_FILES_SIZE = 25 * 1024 * 1024; // 5MB
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "application/pdf", "text/plain"];

const RequestService: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const navigate = useNavigate();
	const location = useLocation();
	const { fromAiChat } = location.state || {};
	const { aiChatStatusValue } = useAiChatStatus();
	const { chatId = "", chatMessages = [] } = aiChatStatusValue;
	const textAreaRef = useRef<any>(null);

	const [initialText, setInitialText] = useState<string>("");
	const [userText, setUserText] = useState<string>("");
	const [selectedPropertyId, setSelectedPropertyId] = useState<string>();
	const [selectedPropertyName, setSelectedPropertyName] = useState<string>();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState<boolean>(false);
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [attachmentsTotalSize, setAttachmentsTotalSize] = useState<number>(0);
	const [error, setError] = useState<string | null>(null);
	const [requestCompleted, setRequestCompleted] = useState<boolean>(false);

	useEffect(() => {
		if (fromAiChat && chatMessages && chatMessages?.length > 0) {
			const chatConversation = chatMessages.reduce((conversation, item, currentIndex) => {
				if (item.isSystem) {
					return `${conversation}\n\nMaestro AI: ${item.message}`;
				} else {
					return `${conversation}\n\n${item.user?.first_name}: ${item.message}`;
				}
			}, "");

			setInitialText(chatConversation);
		} else {
			setInitialText("");
		}
	}, [fromAiChat, chatMessages?.length]);

	// Add click handler for the container
	const handleContainerClick = useCallback((e: React.MouseEvent) => {
		// Only focus if we're not clicking on a button or other interactive element
		const target = e.target as HTMLElement;
		const isButton = target.tagName === "BUTTON";
		const isSelect = target.closest(".ant-select");
		const isAttachment = target.closest(".vertical-container");

		if (!isButton && !isSelect && !isAttachment) {
			textAreaRef.current?.focus();
		}
	}, []);

	const handleGoBack = useCallback(() => {
		if (window.history.length > 2) {
			navigate(-1); // Go to previous route if it exists
		} else {
			navigate(routeNames.DASHBOARD); // Fallback to dashboard
		}
	}, [navigate]);

	const handleCloseClick = useCallback(() => {
		// Only show confirmation if there's text entered
		if (userText.trim()) {
			setIsModalVisible(true);
		} else {
			handleGoBack();
		}
	}, [userText]);

	const handleModalCancel = useCallback(() => {
		setIsModalVisible(false);
	}, []);

	const handleModalConfirm = useCallback(() => {
		setIsModalVisible(false);
		handleGoBack();
	}, [handleGoBack]);

	const fileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const result = reader.result;
				if (typeof result === "string") {
					resolve(result.split(",")[1]);
				} else {
					reject(new Error("Failed to convert file to base64"));
				}
			};
			reader.onerror = error => reject(error);
		});
	};

	const handleOnRemoveAttachment = useCallback(
		(attachmentFile: any) => {
			const newList = [...attachments].filter(element => element.uid !== attachmentFile.uid);

			setAttachments(newList);
		},
		[attachments]
	);

	const updateAttachments = useCallback(
		async (attachment: any) => {
			const { uid, file, name, type, size } = attachment;

			const newSize = attachmentsTotalSize + size;

			// Check file size
			if (newSize > MAX_FILES_SIZE) {
				setError(
					`You can send up to 25 MB of attachments. If you have more than one attachment, the total of those files cannot exceed 25 MB.`
				);
			} else {
				setAttachmentsTotalSize(newSize);
			}

			// Check file type
			if (!ALLOWED_FILE_TYPES.includes(file.type)) {
				setError(`File type "${file.type}" is not allowed.`);
			}

			const content = await fileToBase64(file);

			const newAttachment = {
				uid: uid,
				file,
				filename: name,
				content,
				contentType: type
			};

			let newList: Attachment[] = [];

			setError(null);

			if (attachments && attachments.length > 0) {
				newList = [...attachments].concat([newAttachment]);
			} else {
				newList = [newAttachment];
			}

			setAttachments(newList);
		},
		[attachments]
	);

	const handleOnErrorSendRequestService = useCallback((error: ApolloError) => {
		console.log("sendRequestServiceMutation Error: ", error);
		setError("There was an error sending your request. Please try again.");
	}, []);

	const handleOnCompletedSendRequestService = useCallback((data: any) => {
		// console.log("sendRequestServiceMutation Completed: ", data);
		setRequestCompleted(true);
	}, []);

	const [sendRequestServiceMutation, { data, loading, error: sendRequestError }] = useSendRequestServiceMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorSendRequestService,
		onCompleted: handleOnCompletedSendRequestService
	});

	const handleSendRequest = useCallback(async () => {
		if (selectedPropertyId && userText) {
			try {
				const formattedAttachments = attachments.map(element => {
					const { content, contentType, filename } = element;
					return {
						filename,
						content,
						contentType
					};
				});

				const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

				const response = await sendRequestServiceMutation({
					variables: {
						user_text: userText,
						user_id: loggedUserValue.id,
						user_email: loggedUserValue.email,
						property_id: selectedPropertyId,
						workspace_id: loggedUserValue.workspace_id,
						organization_id: loggedUserValue.organization_id,
						attachments: formattedAttachments,
						initial_text: initialText,
						base_url: baseUrl
					}
				});
			} catch (error) {
				console.error("handleSendRequest Error:", error);
			}
		}
	}, [initialText, userText, loggedUserValue, selectedPropertyId, attachments, sendRequestServiceMutation]);

	const handleOnSelectProperty = useCallback(
		(value: string) => {
			setSelectedPropertyId(value);

			if (value && loggedUserValue.user_properties && loggedUserValue.user_properties.length > 0) {
				const selectedUserProperty = loggedUserValue.user_properties.find(element => element.property_id === value);

				if (selectedUserProperty) {
					setSelectedPropertyName(selectedUserProperty.property?.name);
				}
			} else {
				setSelectedPropertyName("");
			}
		},
		[loggedUserValue.user_properties]
	);

	const resetState = useCallback(() => {
		setSelectedPropertyId("");
		setSelectedPropertyName("");
		setInitialText("");
		setUserText("");
		setIsModalVisible(false);
		setAddAttachmentModalOpen(false);
		setAttachments([]);
		setAttachmentsTotalSize(0);
		setError(null);
		setRequestCompleted(false);
	}, []);

	const propertiesOptions = useMemo(() => {
		if (loggedUserValue.user_properties && loggedUserValue.user_properties.length > 0) {
			const propertyOptionsValues = loggedUserValue.user_properties.map(element => {
				return { value: element.property?.id, label: element.property?.name };
			});

			return propertyOptionsValues;
		}

		return [];
	}, [loggedUserValue.user_properties]);

	return (
		<div className="request-service-screen-container">
			<span className="page-title">{"Service Request"}</span>

			{requestCompleted ? (
				<>
					{selectedPropertyName ? (
						<span className="request-service-screen-sub-title">{`Requesting Service For ${selectedPropertyName}`}</span>
					) : null}

					<div className="request-service-completed-container">
						<div className="request-service-completed">
							<div className="basic-row">
								<span className="request-service-completed-x" onClick={resetState}>
									{"X"}
								</span>
							</div>
							<div className="request-service-completed-message-container">
								<p className="request-service-completed-message">
									{"Maestro has received your \n request and will follow up within 24 hours."}
								</p>
							</div>
							<div className="request-service-completed-button-container">
								<Button type="primary" className="request-service-completed-button" onClick={resetState}>
									{"Send Another Request"}
								</Button>
							</div>
						</div>
					</div>
				</>
			) : (
				<>
					{loggedUserValue.user_properties && loggedUserValue.user_properties.length > 0 ? (
						<div className="request-service-input-container">
							<Select
								value={selectedPropertyId}
								defaultActiveFirstOption={false}
								className="request-service-property-input"
								popupClassName="property-popup"
								onChange={(value: string) => handleOnSelectProperty(value)}
								options={propertiesOptions}
								placeholder="Select Property"
								allowClear
								suffixIcon={
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										alt="dropdown icon"
										className="chevron-button"
									/>
								}
							/>
						</div>
					) : null}

					<div className="email-content-container">
						<div className="request-service-email-container" onClick={handleContainerClick}>
							<div className="email-header-icons">
								<CloseOutlined className="rs-close-icon" onClick={handleCloseClick} />
							</div>
							<div className="email-input-container">
								<div>
									<TextArea
										ref={textAreaRef}
										name="userText"
										className="email-input-textarea"
										placeholder="Please describe your issue here."
										value={userText}
										autoComplete="off"
										onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
											setUserText(event.target.value);
										}}
										allowClear
										autoSize
										disabled={loading}
									/>
								</div>

								{initialText ? (
									<div className="email-initial-text-container">
										<span className="email-initial-title">{`A copy of your chat is included below to help bring the service department up to speed. Please add any additional notes.`}</span>
										<div>
											<Markdown>{initialText}</Markdown>
										</div>
									</div>
								) : null}
							</div>

							<AddAttachmentModal
								open={addAttachmentModalOpen}
								onCancel={() => setAddAttachmentModalOpen(false)}
								updateAttachments={updateAttachments}
								isEmail={true}
								editProduct={false}
							/>

							{error ? <Alert message={error} className="request-service-error" type="error" showIcon /> : null}

							{attachments && attachments.length > 0 ? (
								<div className="vertical-container">
									{attachments.map(attachmentFile => {
										return (
											<AttachmentItem
												key={`product-attachment-item-${attachmentFile.uid}`}
												// name={attachmentFile.name}
												file={attachmentFile.file}
												onRemove={() => {
													if (!loading) {
														handleOnRemoveAttachment(attachmentFile);
													}
												}}
											/>
										);
									})}
								</div>
							) : null}

							<div className="email-buttons-container">
								<button
									className="email-upload-button"
									onClick={() => setAddAttachmentModalOpen(true)}
									disabled={loading}
								>
									Upload
								</button>

								<button
									className="email-send-button"
									onClick={handleSendRequest}
									disabled={loading || userText === "" || selectedPropertyId === ""}
								>
									Send Request
								</button>
							</div>
						</div>
					</div>

					<Modal
						title={null}
						open={isModalVisible}
						onCancel={handleModalCancel}
						footer={null}
						maskClosable={false}
						centered
						styles={{
							mask: { backgroundColor: "rgba(0, 0, 0, 0.45)" },
							content: modalStyles.content
						}}
					>
						<div className="rs-confirmation-modal-title">Discard changes?</div>
						<div className="rs-confirmation-modal-text">
							You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.
						</div>
						<div className="rs-confirmation-modal-footer">
							<Button onClick={handleModalCancel} className="rs-confirmation-modal-cancel-button">
								Cancel
							</Button>
							<Button onClick={handleModalConfirm} className="rs-confirmation-modal-confirm-button">
								Discard
							</Button>
						</div>
					</Modal>
				</>
			)}

			<LoadingOverlay isLoading={loading} />
		</div>
	);
};

export default RequestService;
